<script setup lang="ts">
export interface AvailableFader {
    id: string;
    qlcWidgetId: string;
    name: string;
    faderMode?: 'dmx' | 'time';
}

interface Props {
    widgetId: string;
    configId?: string;
    label: string;
    color?: string;
    disabled?: boolean;
    height?: number;
    mode?: 'auto' | 'dmx' | 'time';
    minMs?: number;
    maxMs?: number;
    linkedQlcId?: string;
    linkedLabel?: string;
    isLinked?: boolean;
    availableFaders?: AvailableFader[];
}

const props = withDefaults(defineProps<Props>(), {
    configId: '',
    color: '#00f0ff',
    disabled: false,
    height: 240,
    mode: 'auto',
    minMs: 0,
    maxMs: 5000,
    linkedQlcId: undefined,
    linkedLabel: undefined,
    isLinked: false,
    availableFaders: () => [],
});

const emit = defineEmits<{
    (e: 'change', value: number): void;
    (e: 'toggle-link', isLinked: boolean): void;
    (e: 'set-link', payload: { targetConfigId: string; targetQlcId: string }): void;
    (e: 'remove-link'): void;
}>();

const { qlcStore, setFaderValue, sendSpeedDialTime, triggerHaptic } = useQlcPlus();

const trackRef = ref<HTMLElement | null>(null);
const isDragging = ref(false);
const showLinkDialog = ref(false);

const isTimeMode = computed(() => {
    if (props.mode === 'time') {
        return true;
    }
    if (props.mode === 'dmx') {
        return false;
    }
    return qlcStore.widgets[props.widgetId]?.type === 'SpeedDial'
        || qlcStore.speedDialStates[props.widgetId] !== undefined;
});

const currentDmxValue = computed(() => {
    return qlcStore.widgets[props.widgetId]?.value ?? 0;
});

const currentTimeMs = computed(() => {
    return qlcStore.speedDialStates[props.widgetId]?.timeMs ?? 0;
});

const percentage = computed(() => {
    if (isTimeMode.value) {
        const max = props.maxMs || 5000;
        return Math.min(100, Math.max(0, Math.round((currentTimeMs.value / max) * 100)));
    }
    return Math.round((currentDmxValue.value / 255) * 100);
});

const displayPrimary = computed(() => {
    if (isTimeMode.value) {
        return `${(currentTimeMs.value / 1000).toFixed(2)}s`;
    }
    return `${currentDmxValue.value}`;
});

const displaySecondary = computed(() => {
    if (isTimeMode.value) {
        return `${currentTimeMs.value} ms`;
    }
    return `${percentage.value}%`;
});

// Presets with 0.1s and 0.25s included
const timePresets = [
    { label: '0s', ms: 0 },
    { label: '0.1s', ms: 100 },
    { label: '0.25s', ms: 250 },
    { label: '0.5s', ms: 500 },
    { label: '1s', ms: 1000 },
    { label: '2s', ms: 2000 },
    { label: '3s', ms: 3000 },
    { label: '5s', ms: 5000 },
];

const dmxPresets = [
    { label: '0%', val: 0 },
    { label: '50%', val: 128 },
    { label: '100%', val: 255 },
];

function calculateValueFromPointer(event: PointerEvent): number {
    if (!trackRef.value) {
        return isTimeMode.value ? currentTimeMs.value : currentDmxValue.value;
    }
    const rect = trackRef.value.getBoundingClientRect();
    const relativeY = rect.bottom - event.clientY;
    const clampedY = Math.max(0, Math.min(rect.height, relativeY));
    const ratio = clampedY / rect.height;

    if (isTimeMode.value) {
        const max = props.maxMs || 5000;
        const raw = Math.round(ratio * max);
        // Step in 25ms increments for fine control
        const snapped = Math.round(raw / 25) * 25;
        if (snapped <= 25) {
            return 0;
        }
        if (snapped >= max - 25) {
            return max;
        }
        return Math.max(props.minMs || 0, Math.min(max, snapped));
    }
    return Math.round(ratio * 255);
}

function applyValue(val: number): void {
    if (isTimeMode.value) {
        sendSpeedDialTime(props.widgetId, val);
        // If linked, also apply to linked partner fader
        if (props.isLinked && props.linkedQlcId) {
            sendSpeedDialTime(props.linkedQlcId, val);
        }
    }
    else {
        setFaderValue(props.widgetId, val);
        // If linked, also apply to linked partner fader
        if (props.isLinked && props.linkedQlcId) {
            setFaderValue(props.linkedQlcId, val);
        }
    }
    emit('change', val);
}

function handlePointerDown(event: PointerEvent): void {
    if (props.disabled || !trackRef.value) {
        return;
    }
    isDragging.value = true;
    try {
        trackRef.value.setPointerCapture(event.pointerId);
    }
    catch {
        // Ignore pointer capture failure on unsupported devices
    }
    const val = calculateValueFromPointer(event);
    applyValue(val);
    triggerHaptic(20);
}

function handlePointerMove(event: PointerEvent): void {
    if (!isDragging.value || props.disabled) {
        return;
    }
    const val = calculateValueFromPointer(event);
    applyValue(val);

    // Haptic tick when reaching min or max boundaries
    const maxVal = isTimeMode.value ? (props.maxMs || 5000) : 255;
    if (val === 0 || val === maxVal) {
        triggerHaptic(30);
    }
}

function handlePointerUp(event: PointerEvent): void {
    if (!isDragging.value) {
        return;
    }
    isDragging.value = false;
    try {
        trackRef.value?.releasePointerCapture(event.pointerId);
    }
    catch {
        // Ignore release error
    }
    const val = calculateValueFromPointer(event);
    applyValue(val);
    triggerHaptic(15);
}

function handlePresetClick(val: number): void {
    if (props.disabled) {
        return;
    }
    applyValue(val);
    triggerHaptic([20, 20]);
}

function toggleLink(): void {
    triggerHaptic(20);
    const newLinkedState = !props.isLinked;
    emit('toggle-link', newLinkedState);
    if (newLinkedState && props.linkedQlcId) {
        // Synchronize linked fader immediately to this fader's current value
        const current = isTimeMode.value ? currentTimeMs.value : currentDmxValue.value;
        if (isTimeMode.value) {
            sendSpeedDialTime(props.linkedQlcId, current);
        }
        else {
            setFaderValue(props.linkedQlcId, current);
        }
    }
}

function selectLinkTarget(target: AvailableFader): void {
    triggerHaptic(20);
    showLinkDialog.value = false;
    emit('set-link', {
        targetConfigId: target.id,
        targetQlcId: target.qlcWidgetId,
    });
    // Immediately sync target to this fader's current value
    const current = isTimeMode.value ? currentTimeMs.value : currentDmxValue.value;
    if (isTimeMode.value) {
        sendSpeedDialTime(target.qlcWidgetId, current);
    }
    else {
        setFaderValue(target.qlcWidgetId, current);
    }
}

function removeLink(): void {
    triggerHaptic(20);
    showLinkDialog.value = false;
    emit('remove-link');
}
</script>

<template>
    <div
        class="dancefloor-fader"
        :class="{
            'dancefloor-fader--dragging': isDragging,
            'dancefloor-fader--disabled': disabled,
            'dancefloor-fader--time': isTimeMode,
            'dancefloor-fader--linked': isLinked && linkedQlcId,
        }"
        :style="{
            '--fader-accent-color': color,
        }"
    >
        <div class="fader-header">
            <div class="fader-label-row">
                <span
                    class="fader-label"
                    :title="label"
                >{{ label }}</span>
                <span
                    v-if="isTimeMode"
                    class="time-mode-badge"
                >
                    SPEED
                </span>
            </div>

            <!-- Fader Link Bar -->
            <div class="fader-link-bar">
                <template v-if="linkedQlcId">
                    <button
                        type="button"
                        class="fader-link-toggle"
                        :class="{ 'fader-link-toggle--active': isLinked }"
                        :title="isLinked ? `Gekoppeld aan ${linkedLabel || 'fader'}. Klik om los te koppelen.` : `Los van ${linkedLabel || 'fader'}. Klik om weer te koppelen.`"
                        @click="toggleLink"
                    >
                        <v-icon
                            :icon="isLinked ? 'mdi-link-variant' : 'mdi-link-variant-off'"
                            size="12"
                        />
                        <span>{{ isLinked ? 'GELINKT' : 'LOS' }}</span>
                    </button>
                    <button
                        type="button"
                        class="fader-link-cfg-btn"
                        title="Koppeling wijzigen"
                        @click="showLinkDialog = true"
                    >
                        <v-icon
                            icon="mdi-dots-vertical"
                            size="12"
                        />
                    </button>
                </template>
                <template v-else-if="availableFaders && availableFaders.length > 0">
                    <button
                        type="button"
                        class="fader-link-add-btn"
                        title="Koppel deze fader aan een andere fader"
                        @click="showLinkDialog = true"
                    >
                        <v-icon
                            icon="mdi-link-variant-plus"
                            size="12"
                        />
                        <span>KOPPEL</span>
                    </button>
                </template>
            </div>

            <div class="fader-readout">
                <span class="dmx-val">{{ displayPrimary }}</span>
                <span class="pct-val">{{ displaySecondary }}</span>
            </div>
        </div>

        <div
            ref="trackRef"
            class="fader-track"
            :style="{
                height: `${height}px`,
            }"
            @pointerdown="handlePointerDown"
            @pointermove="handlePointerMove"
            @pointerup="handlePointerUp"
            @pointercancel="handlePointerUp"
        >
            <div
                class="fader-fill"
                :style="{
                    height: `${percentage}%`,
                }"
            >
                <div class="fader-handle" />
            </div>

            <div class="fader-ticks">
                <template v-if="isTimeMode">
                    <span
                        class="tick tick--time tick--edge"
                        title="5.0s"
                    />
                    <span
                        class="tick tick--time"
                        title="4.0s"
                    />
                    <span
                        class="tick tick--time"
                        title="3.0s"
                    />
                    <span
                        class="tick tick--time tick--mid"
                        title="2.5s"
                    />
                    <span
                        class="tick tick--time"
                        title="2.0s"
                    />
                    <span
                        class="tick tick--time"
                        title="1.0s"
                    />
                    <span
                        class="tick tick--time"
                        title="0.5s"
                    />
                    <span
                        class="tick tick--time tick--edge"
                        title="0.0s"
                    />
                </template>
                <template v-else>
                    <span class="tick tick--100" />
                    <span class="tick tick--75" />
                    <span class="tick tick--50" />
                    <span class="tick tick--25" />
                    <span class="tick tick--0" />
                </template>
            </div>
        </div>

        <div
            class="fader-quick-presets"
            :class="{ 'fader-quick-presets--time': isTimeMode }"
        >
            <template v-if="isTimeMode">
                <button
                    v-for="p in timePresets"
                    :key="p.ms"
                    type="button"
                    class="preset-chip"
                    :class="{ 'preset-chip--active': Math.abs(currentTimeMs - p.ms) <= 25 }"
                    @click="handlePresetClick(p.ms)"
                >
                    {{ p.label }}
                </button>
            </template>
            <template v-else>
                <button
                    v-for="p in dmxPresets"
                    :key="p.val"
                    type="button"
                    class="preset-chip"
                    :class="{ 'preset-chip--active': currentDmxValue === p.val }"
                    @click="handlePresetClick(p.val)"
                >
                    {{ p.label }}
                </button>
            </template>
        </div>

        <!-- Link Fader Dialog -->
        <v-dialog
            v-model="showLinkDialog"
            max-width="360"
        >
            <div class="link-modal-card">
                <div class="link-modal-header">
                    <div class="link-modal-title">
                        <v-icon
                            icon="mdi-link-variant"
                            color="#00f0ff"
                        />
                        <span>KOPPEL FADER</span>
                    </div>
                    <button
                        type="button"
                        class="link-modal-close-btn"
                        @click="showLinkDialog = false"
                    >
                        <v-icon
                            icon="mdi-close"
                            size="16"
                        />
                    </button>
                </div>

                <div class="link-modal-desc">
                    Koppel <strong>{{ label }}</strong> aan een andere fader (bijv. Fade-In en Fade-Uit). Als ze gekoppeld zijn, bewegen ze synchroon.
                </div>

                <div class="link-faders-list">
                    <button
                        v-for="target in availableFaders"
                        :key="target.id"
                        type="button"
                        class="link-target-btn"
                        :class="{ 'link-target-btn--current': target.qlcWidgetId === linkedQlcId }"
                        @click="selectLinkTarget(target)"
                    >
                        <div class="target-info">
                            <span class="target-name">{{ target.name }}</span>
                            <span class="target-sub">ID: {{ target.qlcWidgetId }} ({{ target.faderMode === 'time' ? 'Tijd 0-5s' : 'DMX' }})</span>
                        </div>
                        <v-icon
                            :icon="target.qlcWidgetId === linkedQlcId ? 'mdi-check-circle' : 'mdi-plus-circle-outline'"
                            :color="target.qlcWidgetId === linkedQlcId ? '#00f0ff' : 'rgba(255,255,255,0.4)'"
                            size="18"
                        />
                    </button>
                </div>

                <div
                    v-if="linkedQlcId"
                    class="link-modal-actions"
                >
                    <button
                        type="button"
                        class="unlink-btn"
                        @click="removeLink"
                    >
                        <v-icon
                            icon="mdi-link-variant-off"
                            size="14"
                        />
                        <span>Koppeling verbreken</span>
                    </button>
                </div>
            </div>
        </v-dialog>
    </div>
</template>

<style scoped>
.dancefloor-fader {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 14px 10px;
    background: linear-gradient(145deg, #18182b, #121220);
    border: 2px solid rgba(255, 255, 255, 0.1);
    border-radius: 16px;
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.5);
    user-select: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.dancefloor-fader--linked {
    border-color: rgba(0, 240, 255, 0.35);
}

.fader-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 100%;
    margin-bottom: 8px;
    text-align: center;
    gap: 2px;
}

.fader-label-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    width: 100%;
}

.fader-label {
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #e2e2f5;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.time-mode-badge {
    font-size: 9px;
    font-weight: 900;
    letter-spacing: 0.5px;
    padding: 1px 5px;
    border-radius: 4px;
    background: rgba(255, 170, 0, 0.18);
    color: #ffaa00;
    border: 1px solid rgba(255, 170, 0, 0.35);
    flex-shrink: 0;
}

.fader-link-bar {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    margin: 2px 0 4px;
}

.fader-link-toggle {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 6px;
    font-size: 9px;
    font-weight: 900;
    letter-spacing: 0.5px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.15);
    color: rgba(255, 255, 255, 0.55);
    cursor: pointer;
    transition: all 0.12s ease;
}

.fader-link-toggle:hover {
    background: rgba(255, 255, 255, 0.12);
    color: #ffffff;
}

.fader-link-toggle--active {
    background: rgba(0, 240, 255, 0.18);
    border-color: #00f0ff;
    color: #00f0ff;
    box-shadow: 0 0 8px rgba(0, 240, 255, 0.3);
}

.fader-link-cfg-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    background: transparent;
    border: none;
    color: rgba(255, 255, 255, 0.4);
    cursor: pointer;
    border-radius: 4px;
    transition: all 0.12s ease;
}

.fader-link-cfg-btn:hover {
    color: #ffffff;
    background: rgba(255, 255, 255, 0.1);
}

.fader-link-add-btn {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 2px 7px;
    border-radius: 6px;
    font-size: 9px;
    font-weight: 800;
    background: rgba(255, 255, 255, 0.04);
    border: 1px dashed rgba(255, 255, 255, 0.2);
    color: rgba(255, 255, 255, 0.45);
    cursor: pointer;
    transition: all 0.12s ease;
}

.fader-link-add-btn:hover {
    background: rgba(0, 240, 255, 0.1);
    border-color: #00f0ff;
    color: #00f0ff;
}

.fader-readout {
    display: flex;
    align-items: baseline;
    gap: 6px;
}

.dmx-val {
    font-size: 18px;
    font-weight: 900;
    color: var(--fader-accent-color);
    text-shadow: 0 0 10px var(--fader-accent-color);
}

.pct-val {
    font-size: 12px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.6);
}

.fader-track {
    position: relative;
    width: 58px;
    background: #0d0d17;
    border: 2px solid rgba(255, 255, 255, 0.15);
    border-radius: 29px;
    touch-action: none;
    cursor: pointer;
    overflow: hidden;
    display: flex;
    align-items: flex-end;
    box-shadow: inset 0 2px 10px rgba(0, 0, 0, 0.8);
}

.dancefloor-fader--dragging .fader-track {
    border-color: var(--fader-accent-color);
    box-shadow: inset 0 2px 10px rgba(0, 0, 0, 0.8), 0 0 16px var(--fader-accent-color);
}

.fader-fill {
    position: relative;
    width: 100%;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.2), var(--fader-accent-color));
    border-radius: 0 0 27px 27px;
    transition: height 0.04s linear;
    pointer-events: none;
}

.fader-handle {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 8px;
    background: #ffffff;
    border-radius: 4px;
    box-shadow: 0 0 12px var(--fader-accent-color), 0 0 4px #ffffff;
}

.fader-ticks {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    pointer-events: none;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    padding: 12px 6px;
}

.tick {
    display: block;
    width: 8px;
    height: 2px;
    background: rgba(255, 255, 255, 0.25);
    border-radius: 1px;
}

.tick--50,
.tick--mid {
    width: 14px;
    background: rgba(255, 255, 255, 0.45);
}

.fader-quick-presets {
    display: flex;
    justify-content: space-between;
    width: 100%;
    gap: 6px;
    margin-top: 12px;
}

.fader-quick-presets--time {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 4px;
    margin-top: 8px;
}

.preset-chip {
    padding: 5px 1px;
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    color: rgba(255, 255, 255, 0.75);
    font-size: 10px;
    font-weight: 800;
    cursor: pointer;
    text-align: center;
    transition: all 0.15s ease;
}

.preset-chip:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #ffffff;
}

.preset-chip:active,
.preset-chip--active {
    background: var(--fader-accent-color);
    color: #0a0a14;
    border-color: var(--fader-accent-color);
    font-weight: 900;
    box-shadow: 0 0 10px var(--fader-accent-color);
}

.dancefloor-fader--disabled {
    opacity: 0.45;
    pointer-events: none;
}

/* Modal Dialog for Linking */
.link-modal-card {
    display: flex;
    flex-direction: column;
    background: #141424;
    border: 1px solid rgba(0, 240, 255, 0.3);
    border-radius: 16px;
    padding: 16px;
    gap: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.7), 0 0 20px rgba(0, 240, 255, 0.15);
}

.link-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.link-modal-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    font-weight: 800;
    color: #ffffff;
    letter-spacing: 0.5px;
}

.link-modal-close-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 6px;
    color: rgba(255, 255, 255, 0.8);
    cursor: pointer;
    transition: all 0.12s ease;
}

.link-modal-close-btn:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #ffffff;
}

.link-modal-desc {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.65);
    line-height: 1.4;
}

.link-faders-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 220px;
    overflow-y: auto;
}

.link-target-btn {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 12px;
    background: #18182b;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 8px;
    color: #ffffff;
    cursor: pointer;
    text-align: left;
    transition: all 0.12s ease;
}

.link-target-btn:hover {
    background: #1f1f38;
    border-color: #00f0ff;
}

.link-target-btn--current {
    background: rgba(0, 240, 255, 0.12);
    border-color: #00f0ff;
}

.target-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.target-name {
    font-size: 13px;
    font-weight: 700;
}

.target-sub {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.45);
}

.link-modal-actions {
    display: flex;
    justify-content: flex-end;
    padding-top: 6px;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.unlink-btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 6px 12px;
    background: rgba(255, 34, 85, 0.12);
    border: 1px solid rgba(255, 34, 85, 0.3);
    border-radius: 8px;
    color: #ff3366;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.12s ease;
}

.unlink-btn:hover {
    background: rgba(255, 34, 85, 0.25);
    border-color: #ff3366;
}
</style>
